import {AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {select, Store} from '@ngrx/store';
import {of, Subject} from 'rxjs';
import {catchError, switchMap, take, takeUntil} from 'rxjs/operators';
import {ContactsApiService} from '@features/contacts/store/contacts-api.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {chatFaceStyle} from '../../messenger/chat-face';
import {ChatMember, searchMembers, toChatMembers} from '../../messenger/chat-members';
import {MessengerFlags} from '../../messenger/messenger-flags';
import {MemberStatus} from '../../messenger/messenger.models';
import {defaultGroupName, newChatOutcome} from '../../messenger/new-chat';
import {MessengerPresencePort} from '../../messenger/presence.port';
import {GroupNameDialogComponent} from './group-name-dialog.component';

/** A group the sheet settled: the people ticked, in order, and the name the reader confirmed. */
export interface NewChatGroup {
  members: ChatMember[];
  name: string;
}

/**
 * THE NEW CHAT SHEET (ITP-E-203-04): carbon's overlay, raised over the list it was opened from, on the Chat panel and
 * on the Messages page alike. Search, every colleague A to Z with a box, Continue: one person opens the 1:1, two or more
 * ask for the group's name first. The host creates the conversation (it owns the commands and where they land), and
 * removes the sheet on `closed`, which is what clears the query and the picks. Inviting someone new stays in Contacts.
 */
@Component({
  standalone: false,
  selector: 'chat-new-chat-sheet',
  templateUrl: './new-chat-sheet.component.html',
  styleUrls: ['./new-chat-sheet.component.scss']
})
export class NewChatSheetComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();
  @Output() directPicked = new EventEmitter<ChatMember>();
  @Output() groupPicked = new EventEmitter<NewChatGroup>();
  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;

  readonly faceStyle = chatFaceStyle(44);
  readonly showPresence: boolean;
  query = '';
  state: 'loading' | 'error' | 'ready' = 'loading';
  /** The colleagues the search lets through, A to Z. */
  shown: ChatMember[] = [];
  /** The people ticked, in the order they were ticked: the group's default name follows it. */
  picked: ChatMember[] = [];
  presence: Record<string, MemberStatus> = {};

  private people: ChatMember[] = [];
  private me: string | null = null;
  private readonly load$ = new Subject<void>();
  private readonly unsubscribe$ = new Subject<void>();

  constructor(private contactsApi: ContactsApiService,
              private securityStore: Store<SecurityState>,
              private presencePort: MessengerPresencePort,
              private dialog: MatDialog,
              messengerFlags: MessengerFlags) {
    this.showPresence = messengerFlags.isOn('B-15');
  }

  ngOnInit() {
    this.securityStore.pipe(select(getUserInfo), takeUntil(this.unsubscribe$)).subscribe(user => {
      this.me = user?.email || null;
      this.refresh();
    });
    this.load$.pipe(
      switchMap(() => {
        this.state = 'loading';
        return this.contactsApi.getAllSharedContacts().pipe(catchError(() => {
          this.state = 'error';
          return of(null);
        }));
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      if (this.state === 'error') {
        return;
      }
      this.people = toChatMembers(response);
      this.state = 'ready';
      this.refresh();
      this.readPresence();
    });
    this.load();
  }

  ngAfterViewInit() {
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  load() {
    this.load$.next();
  }

  get canContinue(): boolean {
    return newChatOutcome(this.picked) !== 'disabled';
  }

  isPicked(member: ChatMember): boolean {
    return this.picked.some(pick => pick.email === member.email);
  }

  togglePicked(member: ChatMember) {
    this.picked = this.isPicked(member)
      ? this.picked.filter(pick => pick.email !== member.email)
      : [...this.picked, member];
  }

  onSearch(text: string) {
    this.query = text || '';
    this.refresh();
  }

  statusOf(member: ChatMember): MemberStatus | null {
    return this.showPresence ? this.presence[(member.email || '').toLowerCase()] || null : null;
  }

  trackByEmail(index: number, member: ChatMember): string {
    return member.email;
  }

  close() {
    this.closed.emit();
  }

  continue() {
    switch (newChatOutcome(this.picked)) {
      case 'direct':
        this.directPicked.emit(this.picked[0]);
        break;
      case 'name-group':
        this.dialog.open(GroupNameDialogComponent, {width: '420px', data: {name: defaultGroupName(this.picked)}})
          .afterClosed().pipe(take(1), takeUntil(this.unsubscribe$)).subscribe((name: string) => {
            if (name) {
              this.groupPicked.emit({members: [...this.picked], name});
            }
          });
        break;
    }
  }

  /** B-15: one read of the colleagues' statuses, for the dots. */
  private readPresence() {
    if (!this.showPresence || !this.people.length) {
      return;
    }
    this.presencePort.statuses(this.people.map(person => ({email: person.email, companyId: person.companyId}))).pipe(
      take(1),
      catchError(() => of({} as Record<string, MemberStatus>)),
      takeUntil(this.unsubscribe$)
    ).subscribe(statuses => this.presence = statuses || {});
  }

  /** Everybody but the reader, matching the search, A to Z. XatBot is not offered: it is never in the roster. */
  private refresh() {
    this.shown = searchMembers(this.people, this.query, this.me)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'}));
  }
}
